import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useShipmentContractFeed } from '#/entities/shipment'
import { TOOLBAR_QUERY_KEY_PREFIX } from '#/features/shipment-toolbar'
import type { useCreateToolbarItem } from '#/features/shipment-toolbar'
import { ContractCreateBodyContractType } from '#/shared/api/generated/shipment/models'
import {
  useGetShipmentApiV1ShipmentsShipmentIdGet,
  useSendContractApiV1ContractsContractIdSendPost,
  useUpdateContractApiV1ContractsContractIdPatch,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

interface ContractSliceDeps {
  shipmentId: string | undefined
  armed: boolean
  createContract: ReturnType<typeof useCreateToolbarItem>['createContract']
  isPending: boolean
}

export function useContractSlice({
  shipmentId,
  armed,
  createContract,
  isPending,
}: ContractSliceDeps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { contracts, isLoading } = useShipmentContractFeed(shipmentId ?? '', {
    enabled: armed && Boolean(shipmentId),
  })

  const shipmentQuery = useGetShipmentApiV1ShipmentsShipmentIdGet(
    shipmentId ?? '',
    { query: { enabled: armed && Boolean(shipmentId) } },
  )
  const customerEmail = shipmentQuery.data?.data.customer?.email ?? ''

  const invalidateToolbar = () =>
    queryClient.invalidateQueries({
      predicate: (query) => {
        const head = query.queryKey[0]
        return (
          typeof head === 'string' && head.startsWith(TOOLBAR_QUERY_KEY_PREFIX)
        )
      },
    })

  const sendMutation = useSendContractApiV1ContractsContractIdSendPost({
    mutation: {
      onSuccess: () => {
        invalidateToolbar()
        toast.success(t('shipmentToolbar.create.contractSuccess'))
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? t('shipmentToolbar.create.error'),
        )
      },
    },
  })

  const updateMutation = useUpdateContractApiV1ContractsContractIdPatch({
    mutation: {
      onSuccess: () => invalidateToolbar(),
    },
  })

  const isSending = isPending || sendMutation.isPending

  const onSend = async () => {
    if (!shipmentId || isSending) return
    if (!customerEmail) {
      toast.error(t('shipmentToolbar.create.customerEmailMissing'))
      return
    }
    try {
      const created = await createContract({
        contractType: ContractCreateBodyContractType.shipping_order,
      })
      await sendMutation.mutateAsync({
        contractId: created.data.id,
        data: { sent_to_email: customerEmail },
      })
    } catch {
      // Both mutations toast their own failure; the click handler discards
      // this promise, so a rethrow would only be an unhandled rejection.
    }
  }

  const onSaveNote = async (contractId: string, note: string) => {
    try {
      await updateMutation.mutateAsync({ contractId, data: { note } })
      toast.success(t('editor.contract.noteSuccess'))
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t('editor.contract.noteError'))
      throw error
    }
  }

  const onToggleCca = async (contractId: string, next: boolean) => {
    try {
      await updateMutation.mutateAsync({ contractId, data: { cca: next } })
    } catch (error) {
      // No rethrow: unlike onSaveNote (whose popover awaits and stays open on
      // failure), the toggle's onChange discards the promise — the toast is
      // the whole failure story, and the un-invalidated query keeps the pill.
      toast.error(getApiErrorDetail(error) ?? t('editor.contract.ccaError'))
    }
  }

  return {
    items: contracts,
    isLoading,
    isSending,
    onSend,
    onSaveNote,
    onToggleCca,
    canSend: Boolean(shipmentId && customerEmail),
  }
}
